import { apiHelpers } from './api';

// Types
export type DrinkingGameType =
  | 'NEVER_HAVE_I_EVER'
  | 'TRUTH_OR_DARE'
  | 'KINGS_CUP'
  | 'BEER_PONG'
  | 'FLIP_CUP'
  | 'RIDE_THE_BUS'
  | 'MOST_LIKELY'
  | 'WOULD_YOU_RATHER'
  | 'CATEGORIES'
  | 'CUSTOM';

export type DrinkingSessionStatus = 'WAITING' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';

export type DrinkingActionType =
  | 'QUESTION_ANSWERED'
  | 'DARE_COMPLETED'
  | 'DARE_REFUSED'
  | 'RULE_TRIGGERED'
  | 'DRINK_ASSIGNED'
  | 'TURN_SKIPPED'
  | 'GAME_EVENT';

export interface DrinkingRules {
  baseRule: string;
  specialRules?: SpecialRule[];
  drinkAmounts: {
    light: number;
    medium: number;
    heavy: number;
  };
}

export interface SpecialRule {
  condition: string;
  action: string;
  drinkAmount: number;
}

export interface GameQuestion {
  id: string;
  text: string;
  category?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
}

export interface GameTask {
  id: string;
  text: string;
  type: 'truth' | 'dare' | 'challenge';
  difficulty?: 'easy' | 'medium' | 'hard';
  drinkPenalty?: number;
}

export interface DrinkingGame {
  id: string;
  name: string;
  gameType: DrinkingGameType;
  description?: string;
  minPlayers: number;
  maxPlayers?: number;
  rules: string;
  drinkingRules: DrinkingRules;
  questions?: GameQuestion[];
  tasks?: GameTask[];
  isActive: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  creator?: {
    username: string;
    displayName?: string;
  };
}

export interface GameState {
  usedQuestions: string[];
  usedTasks: string[];
  playerScores?: Record<string, number>;
  customData?: Record<string, unknown>;
}

export interface DrinkingGamePlayer {
  id: string;
  sessionId: string;
  userId: string;
  username: string;
  displayName?: string;
  joinedAt: string;
  leftAt?: string;
  totalDrinks: number;
  isActive: boolean;
  playerOrder?: number;
  isCurrent: boolean;
}

export interface DrinkingGameSession {
  id: string;
  gameId: string;
  gameName: string;
  gameType: DrinkingGameType;
  createdBy: string;
  status: DrinkingSessionStatus;
  currentRound: number;
  currentPlayer?: string;
  gameState?: GameState;
  startedAt?: string;
  endedAt?: string;
  createdAt: string;
  creator: {
    username: string;
    displayName?: string;
  };
  players: DrinkingGamePlayer[];
  currentQuestion?: GameQuestion;
  currentTask?: GameTask;
}

export interface DrinkingGameAction {
  id: string;
  sessionId: string;
  playerId: string;
  playerName: string;
  actionType: DrinkingActionType;
  actionData?: Record<string, unknown>;
  drinkAmount: number;
  targetPlayers: string[];
  createdAt: string;
}

export interface SessionStats {
  sessionId: string;
  totalRounds: number;
  totalActions: number;
  totalDrinks: number;
  playerStats: {
    userId: string;
    username: string;
    drinks: number;
    actions: number;
  }[];
}

// Input types
export interface CreateDrinkingGameInput {
  name: string;
  gameType: DrinkingGameType;
  description?: string;
  minPlayers?: number;
  maxPlayers?: number;
  rules: string;
  drinkingRules: DrinkingRules;
  questions?: GameQuestion[];
  tasks?: GameTask[];
}

export interface UpdateDrinkingGameInput {
  name?: string;
  description?: string;
  minPlayers?: number;
  maxPlayers?: number;
  rules?: string;
  drinkingRules?: DrinkingRules;
  questions?: GameQuestion[];
  tasks?: GameTask[];
  isActive?: boolean;
}

export interface CreateSessionInput {
  gameId: string;
}

export interface GameActionInput {
  actionType: DrinkingActionType;
  actionData?: Record<string, unknown>;
  drinkAmount?: number;
  targetPlayers?: string[];
}

// ============ Game CRUD Operations ============

/**
 * Create a new drinking game
 */
export const createGame = async (data: CreateDrinkingGameInput): Promise<DrinkingGame> => {
  return apiHelpers.post<DrinkingGame>('/drinking-games/games', data);
};

/**
 * Get all drinking games
 */
export const getGames = async (
  gameType?: DrinkingGameType,
  activeOnly: boolean = true
): Promise<DrinkingGame[]> => {
  const params = new URLSearchParams();
  if (gameType) params.append('gameType', gameType);
  if (activeOnly) params.append('isActive', 'true');

  const queryString = params.toString();
  const url = `/drinking-games/games${queryString ? `?${queryString}` : ''}`;
  return apiHelpers.get<DrinkingGame[]>(url);
};

/**
 * Get a specific game by ID
 */
export const getGameById = async (gameId: string): Promise<DrinkingGame> => {
  return apiHelpers.get<DrinkingGame>(`/drinking-games/games/${gameId}`);
};

/**
 * Update a drinking game
 */
export const updateGame = async (
  gameId: string,
  data: UpdateDrinkingGameInput
): Promise<DrinkingGame> => {
  return apiHelpers.put<DrinkingGame>(`/drinking-games/games/${gameId}`, data);
};

/**
 * Delete a drinking game
 */
export const deleteGame = async (gameId: string): Promise<void> => {
  await apiHelpers.delete(`/drinking-games/games/${gameId}`);
};

// ============ Session Operations ============

/**
 * Create a new game session
 */
export const createSession = async (gameId: string): Promise<DrinkingGameSession> => {
  return apiHelpers.post<DrinkingGameSession>(`/drinking-games/games/${gameId}/sessions`, {});
};

/**
 * Get a specific session by ID
 */
export const getSession = async (sessionId: string): Promise<DrinkingGameSession> => {
  return apiHelpers.get<DrinkingGameSession>(`/drinking-games/sessions/${sessionId}`);
};

/**
 * Join a game session
 */
export const joinSession = async (sessionId: string): Promise<DrinkingGameSession> => {
  return apiHelpers.post<DrinkingGameSession>(`/drinking-games/sessions/${sessionId}/join`, {});
};

/**
 * Leave a game session
 */
export const leaveSession = async (sessionId: string): Promise<DrinkingGameSession> => {
  return apiHelpers.post<DrinkingGameSession>(`/drinking-games/sessions/${sessionId}/leave`, {});
};

/**
 * Start a game session (host only)
 */
export const startSession = async (sessionId: string): Promise<DrinkingGameSession> => {
  return apiHelpers.post<DrinkingGameSession>(`/drinking-games/sessions/${sessionId}/start`, {});
};

/**
 * End/Cancel a game session (host only)
 */
export const endSession = async (sessionId: string): Promise<DrinkingGameSession> => {
  return apiHelpers.post<DrinkingGameSession>(`/drinking-games/sessions/${sessionId}/end`, {});
};

/**
 * Submit a game action
 */
export const submitAction = async (
  sessionId: string,
  data: GameActionInput
): Promise<DrinkingGameAction> => {
  return apiHelpers.post<DrinkingGameAction>(`/drinking-games/sessions/${sessionId}/actions`, data);
};

/**
 * Get session statistics
 */
export const getSessionStats = async (sessionId: string): Promise<SessionStats> => {
  return apiHelpers.get<SessionStats>(`/drinking-games/sessions/${sessionId}/stats`);
};

/**
 * Get active sessions (for lobby list)
 */
export const getActiveSessions = async (): Promise<DrinkingGameSession[]> => {
  return apiHelpers.get<DrinkingGameSession[]>('/drinking-games/sessions/active');
};

// ============ Helper Functions ============

/**
 * Get display name for game type
 */
export const getGameTypeDisplayName = (gameType: DrinkingGameType): string => {
  const names: Record<DrinkingGameType, string> = {
    NEVER_HAVE_I_EVER: 'Never Have I Ever',
    TRUTH_OR_DARE: 'Truth or Dare',
    KINGS_CUP: 'Kings Cup',
    BEER_PONG: 'Beer Pong',
    FLIP_CUP: 'Flip Cup',
    RIDE_THE_BUS: 'Ride The Bus',
    MOST_LIKELY: 'Most Likely To',
    WOULD_YOU_RATHER: 'Would You Rather',
    CATEGORIES: 'Categories',
    CUSTOM: 'Custom Game',
  };
  return names[gameType] || gameType;
};

/**
 * Get emoji for game type
 */
export const getGameTypeEmoji = (gameType: DrinkingGameType): string => {
  const emojis: Record<DrinkingGameType, string> = {
    NEVER_HAVE_I_EVER: '🙅',
    TRUTH_OR_DARE: '🎭',
    KINGS_CUP: '👑',
    BEER_PONG: '🏓',
    FLIP_CUP: '🥤',
    RIDE_THE_BUS: '🚌',
    MOST_LIKELY: '🎯',
    WOULD_YOU_RATHER: '🤔',
    CATEGORIES: '📚',
    CUSTOM: '🎮',
  };
  return emojis[gameType] || '🎮';
};

/**
 * Get status display info
 */
export const getSessionStatusInfo = (status: DrinkingSessionStatus): { label: string; color: string } => {
  const info: Record<DrinkingSessionStatus, { label: string; color: string }> = {
    WAITING: { label: 'Waiting', color: 'yellow' },
    ACTIVE: { label: 'In Progress', color: 'green' },
    PAUSED: { label: 'Paused', color: 'orange' },
    COMPLETED: { label: 'Completed', color: 'gray' },
    CANCELLED: { label: 'Cancelled', color: 'red' },
  };
  return info[status] || { label: status, color: 'gray' };
};
